import React, { useEffect, useRef, ChangeEvent } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { gaImpressionsProduct } from 'ga-events/ga-impressions-product';
import { dimensionsAtom, TComplimentaryProductInfo } from 'pages/dimensions/store/dimensions';
import { addNewCountValue } from 'common/helpers/add-new-count-value.helper';
import { getIsCheckedProducts } from 'common/helpers/get-is-checked-products.helper';
import { complimentaryProductsState } from 'pages/calculation/store/calculation';
import { ComplementaryProduct as ComplementaryProductCommon } from 'pages/common/components/ComplementaryProduct';
import { ComplimentaryProductInfoBlock, VariantsWrapper } from '../styles';
import { Checkbox } from '../Checkbox';
import { Variants } from './components/Variants';
import { changeComplimentaryProductCountValue } from './helpers';

type TComplementaryProductComponent = {
  product: TComplimentaryProductInfo;
  index: number;
  variants?: TComplimentaryProductInfo[];
};

export const ComplementaryProductComponent = ({
  product,
  index,
  variants,
}: TComplementaryProductComponent) => {
  const productRef = useRef<HTMLDivElement>(null);

  const [dimensionState, setDimensionState] = useRecoilState(dimensionsAtom);
  const setComplimentaryProducts = useSetRecoilState(complimentaryProductsState);

  const displayedName = product.displayedName.value?.[0] ?? '';

  const hasVariants = variants && variants.length > 1;

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          gaImpressionsProduct({
            name: displayedName,
            id: product.productId,
            price: `${product.salesPriceValueInConsumerUnitValue ?? 0}`,
            position: `${index + 2}`,
          });
        }
      },

      {
        threshold: 0.5,
      }
    );

    if (productRef.current) {
      observer.observe(productRef.current);
    }

    return () => {
      if (productRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(productRef.current);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- Нужно послать только один раз
  }, []);

  const addCount = (_: ChangeEvent<HTMLInputElement>, value: string) => {
    setDimensionState(prevState => ({
      ...prevState,
      complimentaryProductInfo: addNewCountValue({ prevState, newValue: Number(value), product }),
    }));
    setComplimentaryProducts(prevState =>
      changeComplimentaryProductCountValue({ prevState, newCountValue: value, product })
    );
  };

  const isDisable = !getIsCheckedProducts({
    checkedProducts: dimensionState.checkedProducts,
    productId: product.productId,
  });

  return (
    <ComplimentaryProductInfoBlock ref={productRef}>
      <ComplementaryProductCommon
        photo={product.mediaMainPhoto.value?.[0]}
        displayedName={displayedName}
        productId={product.productId}
        count={product.total.requiredQuantity}
        price={product.salesPriceValueInConsumerUnitValue}
        productPageName={product.product_page_name.value?.[0]}
        isDisabled={isDisable}
        onChangeCounter={addCount}
        variantsComponent={
          hasVariants ? (
            <VariantsWrapper>
              <Variants variants={variants} productId={product.productId} />
            </VariantsWrapper>
          ) : undefined
        }
        checkboxComponent={<Checkbox productId={product.productId} />}
      />
    </ComplimentaryProductInfoBlock>
  );
};
